import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { booksKeys } from "@/features/books/queries/booksQueryKeys";
import booksService from "@/features/books/services/booksService";
import type { UpdateBookInput } from "@/features/books/types";

/**
 * Mutation hook for updating an existing book
 * Invalidates books queries on success and navigates to books page
 */
export function useUpdateBook() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: async ({ bookId, bookData }: { bookId: string; bookData: UpdateBookInput }) => {
            return await booksService.updateBook(bookId, bookData);
        },
        onError: (error) => {
            // Handle specific error cases
            if (error instanceof Error && error.message.includes("not found")) {
                toast.error("Book not found");
            } else if (error instanceof Error && error.message.includes("already exists")) {
                toast.error("A book with this ISBN or title/author combination already exists");
            } else {
                const errorMessage =
                    error instanceof Error ? error.message : "Failed to update book. Please try again.";
                toast.error(errorMessage);
            }
        },
        onSuccess: (_, variables) => {
            // Invalidate both the list and the specific book detail query
            queryClient.invalidateQueries({
                queryKey: booksKeys.all,
            });
            queryClient.invalidateQueries({
                queryKey: booksKeys.detail(variables.bookId),
            });

            toast.success("Book updated successfully");
            navigate("/books");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
